import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { AiOutlineDownCircle } from "react-icons/ai";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import Label from "../../../../../../components/form_fields/Label";
import OrangeButton from "../../../../../../components/buttons/OrangeButton";
import { useDeleteAssignedAsset, useUpdateAssignedAsset } from "../../../../../../services/assigned-asset.service";
import { AssignedAssetDto } from "../../../../../../models/asset-assign.dto";
import { AssetTypeDto } from "../../../../../../models/assets-type.dto";
import { compareDateFields } from "../../../../../../helpers/appHelpers";
import DeleteModal from "../../../../../../components/modals/DeleteModal";

interface IFormInputs {
    assetTypeId: number;
    serialNumber: string;
    dateAssigned: string;
    dateReturned: string;
}

interface IEditAsset {
	asset: AssignedAssetDto;
	assetTypes: AssetTypeDto[];
}

const EditAsset = ({asset, assetTypes}: IEditAsset) => {
    const [expanded, setExpanded] = useState(false);
	const [formDisabled, setFormDisabled] = useState(true);
	const { mutate: updateassets } = useUpdateAssignedAsset();
	const [uploading, setUploading] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	const {
		register,
		watch,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			assetTypeId: asset.assetTypeId,
			serialNumber: asset.serialNumber,
			dateAssigned: asset.dateAssigned?.slice(0, 10),
			dateReturned: asset.dateReturned?.slice(0, 10),
		}
	});
	
	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		updateassets(
			{ payload: data, id: asset.id },  
			{
				onSuccess: () => setFormDisabled(true),
				onSettled: () => setUploading(false),
			}
		)
	};
	
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteAssignedAsset } = useDeleteAssignedAsset();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteAssignedAsset(
			{ id: asset?.id },
			{
				onSuccess: () => setOpenDeleteModal(false),
				onSettled: () => setDeleting(false),
			}
		);
	};

	return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(prev => !prev)}
            classes={{
                root: "border border-gray-300 rounded-lg py-2 mb-2",
            }}
        >
			<AccordionSummary
				expandIcon={<AiOutlineDownCircle className="text-xl" />}
				aria-controls={asset.id + "bh-content"}
				id={asset.id + "bh-header"}
				sx={{ px: 3, border: "1px" }}
			>
				<h2 className="font-inter text-sm font-semibold">
					{asset.serialNumber}
				</h2>
			</AccordionSummary>
			<AccordionDetails sx={{ px: 3 }}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<>
						<div className="border-b-[1.5px] pb-3 flex justify-between font-semibold text-2xs">
							<h2 className="font-semibold text-2xs">Details</h2>
							<button
								type="button"
								className={`border-none text-primary-blue text-2xs ${
									!formDisabled ? "hidden" : ""
								}`}
								onClick={() => setFormDisabled(false)}
							>
								Edit
							</button>
						</div>
						<div className="grid grid-cols-2 gap-x-4 gap-y-6 mt-4 ">
							<div className="w-full">
								<Label title="Asset Type" for="assetTypeId" />
								<select
									defaultValue={asset.assetTypeId}
									disabled={formDisabled}
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors.assetTypeId
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
									{...register("assetTypeId", {
										required: "Select asset type here",
									})}
								>
									<option value="">
										Select Type
									</option>
									{assetTypes.map((assetType) => (
										<option 
											key={assetType.id}
											value={assetType.id}
											className="capitalize"
										>
											{assetType.name}
										</option>
									))}
								</select>
								{errors.assetTypeId && (
									<p className="text-red-500 text-sm mt-1">{errors.assetTypeId.message}</p>
								)}
							</div>
							<div className="w-full">
								<Label title="Serial Number" for="serialNumber" />
								<input
									defaultValue={asset.serialNumber}
									disabled={formDisabled}
									placeholder={"Enter serial number"}
									type="text"
									{...register("serialNumber", {
										required: "Enter serial number here",
									})}
									id="serialNumber"
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors.serialNumber
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors.serialNumber && (
									<p className="text-red-500 text-sm mt-1">
										{errors.serialNumber.message}
									</p>
								)}
							</div>
							<div className="w-full">
								<Label title="Assigned Date" for="dateAssigned" />
								<input
									disabled={formDisabled}
									placeholder={"mm/dd/yyyy"}
									type="date"
									{...register("dateAssigned", {
										required: "Enter assigned date here",
									})}
									id="dateAssigned"
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors.dateAssigned
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors.dateAssigned && (
									<p className="text-red-500 text-sm mt-1">
										{errors.dateAssigned.message}
									</p>
								)}
							</div>
							<div className="w-full">
								<Label title="End Date" for="dateReturned" />
								<input
									disabled={formDisabled}
									placeholder={"mm/dd/yyyy"}
									type="date"
									{...register("dateReturned", {
										required: "Enter end date here",
										validate: (value) => compareDateFields(
											watch("dateAssigned"), 
											value,
											"Assigned date must be less than end date"
										),
									})}
									id="dateReturned"
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors.dateReturned
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors.dateReturned && (
									<p className="text-red-500 text-sm mt-1">
										{errors.dateReturned.message}
									</p>
								)}
							</div>
						</div>
					</>
					
					{!formDisabled && (
						<div className="flex w-full justify-end space-x-4 items-center mt-10">
							<button
								type="button"
								className="px-12 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
								onClick={() => {
									setFormDisabled(true);
									reset();
								}}
							>
								Cancel
							</button>
							<OrangeButton
								title={`${uploading ? "Saving..." : "Save"}`}
								className={"px-12"}
								type={"submit"}
								disabled={uploading}
							/>
						</div>
					)}
					
					{formDisabled && (
						<div className="flex w-full justify-end space-x-4 items-center mt-10">
							<button
								type="button"
								onClick={() => setOpenDeleteModal(true)}
								className={`px-12 py-4 border text-white hover:shadow-sm bg-secondary-red rounded-lg text-2xs ${uploading && "opacity-50"}`}
								disabled={uploading}
							>
								Delete
							</button>
						</div>
					)}

					<DeleteModal
						openDeleteModal={openDeleteModal}
						spanContent={asset.serialNumber}
						setOpenDeleteModal={setOpenDeleteModal}
						handleDeleteMethod={handleDeleteMethod}
						deleting={deleting}
					/>
				</form>
			</AccordionDetails>
		</Accordion>
	);
};

export default EditAsset;
